import React, { useState, useEffect } from "react";
import { Box, Container, Grid, AppBar, Toolbar, Typography, Button, Tooltip, Avatar, IconButton, Chip, useTheme } from "@mui/material";
import { Home as HomeIcon, User, LogOut, Settings, ChevronRight, Moon, Sun, Plus, Search, RefreshCw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import languageLearningApi from "../middleware/languageLearningApi";

import CoursesPanel from "./panels/CoursesPanel";
import SectionsPanel from "./panels/SectionsPanel";
import CourseletsPanel from "./panels/CourseletsPanel";
import SentencesPanel from "./panels/SentencesPanel";

const AdminDashboard = () => {
  const theme = useTheme();
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedCourseletId, setSelectedCourseletId] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(false);

  // Data states for each entity
  const [courses, setCourses] = useState([]);
  const [sections, setSections] = useState([]);
  const [courselets, setCourselets] = useState([]);
  const [sentences, setSentences] = useState([]);

  // Fetch courses on mount
  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await languageLearningApi.get('/courses');
      const data = response.data;
      if (data.success) {
        setCourses(data.courses);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch sections when a course is selected
  useEffect(() => {
    if (selectedCourseId) {
      fetchSections(selectedCourseId);
    } else {
      setSections([]);
    }
  }, [selectedCourseId]);

  const fetchSections = async (courseId) => {
    try {
      setLoading(true);
      const response = await languageLearningApi.get(`/courses/${courseId}/sections`);
      const data = response.data;
      if (data.success) {
        setSections(data.sections);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch courselets when a section is selected
  useEffect(() => {
    if (selectedSectionId) {
      fetchCourselets(selectedSectionId);
    } else {
      setCourselets([]);
    }
  }, [selectedSectionId]);

  const fetchCourselets = async (sectionId) => {
    try {
      setLoading(true);
      const response = await languageLearningApi.get(`/courselets/${sectionId}/courselets`);
      const data = response.data;
      if (data.success) {
        setCourselets(data.courselets);
      }
    } catch (error) {
      console.error("Error fetching courselets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch sentences when a courselet is selected
  useEffect(() => {
    if (selectedCourseletId) {
      fetchSentences(selectedCourseletId);
    } else {
      setSentences([]);
    }
  }, [selectedCourseletId]);

  const fetchSentences = async (courseletId) => {
    try {
      setLoading(true);
      const response = await languageLearningApi.get(`/courselets/${courseletId}/sentences`);
      const data = response.data;
      if (data.success) {
        setSentences(data.sentences);
      }
    } catch (error) {
      console.error("Error fetching sentences:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handlers for selection changes
  const handleCourseSelect = (courseId) => {
    setSelectedCourseId(courseId);
    setSelectedSectionId(null);
    setSelectedCourseletId(null);
  };

  const handleSectionSelect = (sectionId) => {
    setSelectedSectionId(sectionId);
    setSelectedCourseletId(null);
  };

  const handleCourseletSelect = (courseletId) => {
    setSelectedCourseletId(courseletId);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Breadcrumb navigation
  const renderBreadcrumbs = () => {
    const selectedCourse = courses.find(c => c.id === selectedCourseId);
    const selectedSection = sections.find(s => s.id === selectedSectionId);
    const selectedCourselet = courselets.find(c => c.id === selectedCourseletId);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', my: 2, overflow: 'auto', whiteSpace: 'nowrap' }}>
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Chip 
            icon={<HomeIcon size={16} />} 
            label="Dashboard" 
            onClick={() => {
              setSelectedCourseId(null);
              setSelectedSectionId(null);
              setSelectedCourseletId(null);
            }}
            clickable
            color="primary"
            sx={{ mr: 1, cursor: 'pointer' }}
          />
        </motion.div>
        
        {selectedCourse && (
          <>
            <ChevronRight size={16} color={theme.palette.text.secondary} />
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Chip 
                label={selectedCourse.name} 
                onClick={() => {
                  setSelectedSectionId(null);
                  setSelectedCourseletId(null);
                }}
                clickable
                variant="outlined"
                color="primary"
                sx={{ mx: 1, cursor: 'pointer' }}
              />
            </motion.div>
          </>
        )}
        
        {selectedSection && (
          <>
            <ChevronRight size={16} color={theme.palette.text.secondary} />
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Chip 
                label={selectedSection.name} 
                onClick={() => {
                  setSelectedCourseletId(null);
                }}
                clickable
                variant="outlined"
                color="primary"
                sx={{ mx: 1, cursor: 'pointer' }}
              />
            </motion.div>
          </>
        )}
        
        {selectedCourselet && (
          <>
            <ChevronRight size={16} color={theme.palette.text.secondary} />
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Chip 
                label={selectedCourselet.name} 
                variant="outlined"
                color="primary"
                sx={{ mx: 1 }}
              />
            </motion.div>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ 
      bgcolor: darkMode ? '#1a1a2e' : '#f8fafc', 
      color: darkMode ? '#f0f0f0' : 'inherit',
      minHeight: '100vh',
      transition: 'all 0.5s ease'
    }}>
      <AppBar position="static" sx={{ 
        background: darkMode 
          ? "linear-gradient(90deg, #16213e 0%, #0f3460 100%)" 
          : "linear-gradient(90deg, #4776E6 0%, #8E54E9 100%)",
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        transition: 'all 0.5s ease'
      }}>
        <Toolbar>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}
          >
            <HomeIcon size={24} style={{ marginRight: 12 }} />
            <Typography variant="h5" fontWeight="bold" sx={{ 
              background: 'linear-gradient(90deg, #ffffff, #e0e0e0)', 
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              LinguaLearn Admin
            </Typography>
          </motion.div>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Tooltip title="Toggle Dark Mode">
                <IconButton 
                  color="inherit" 
                  onClick={toggleDarkMode}
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                >
                  {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                </IconButton>
              </Tooltip>
            </motion.div>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Tooltip title="Settings">
                <IconButton 
                  color="inherit"
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                >
                  <Settings size={20} />
                </IconButton>
              </Tooltip>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Button 
                variant="contained" 
                startIcon={<User size={18} />}
                endIcon={<LogOut size={16} />}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)', 
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                  textTransform: 'none',
                  px: 2,
                  borderRadius: '24px'
                }}
              >
                Admin
              </Button>
            </motion.div>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {renderBreadcrumbs()}
        
        {/* {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            >
              <RefreshCw size={40} color={darkMode ? "#8E54E9" : "#4776E6"} />
            </motion.div>
          </Box>
        )} */}
        
        <AnimatePresence>
          <motion.div
            key="courses"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CoursesPanel 
                  courses={courses}
                  onRefresh={fetchCourses}
                  onSelectCourse={handleCourseSelect}
                  darkMode={darkMode}
                />
              </Grid>
              
              {selectedCourseId && (
                <Grid item xs={12}>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 1 }}
                  >
                    <SectionsPanel 
                      selectedCourseId={selectedCourseId}
                      sections={sections}
                      onRefresh={fetchSections}
                      onSelectSection={handleSectionSelect}
                      darkMode={darkMode}
                    />
                  </motion.div>
                </Grid>
              )}
              
              {selectedSectionId && (
                <Grid item xs={12}>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 1 }}
                  >
                    <CourseletsPanel 
                      selectedSectionId={selectedSectionId}
                      courselets={courselets}
                      onRefresh={fetchCourselets}
                      onSelectCourselet={handleCourseletSelect}
                      darkMode={darkMode}
                    />
                  </motion.div>
                </Grid>
              )}
              
              {selectedCourseletId && (
                <Grid item xs={12}>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 1 }}
                  >
                    <SentencesPanel 
                      selectedCourseletId={selectedCourseletId}
                      sentences={sentences}
                      onRefresh={fetchSentences}
                      darkMode={darkMode}
                    />
                  </motion.div>
                </Grid>
              )}
            </Grid>
          </motion.div>
        </AnimatePresence>
      </Container>
    </Box>
  );
};

export default AdminDashboard;